import React, { useState } from 'react';
import { Form, Input, Button, Typography, message, Row, Col, Tabs, Card } from 'antd';
import { UserOutlined, LockOutlined, BankOutlined, MailOutlined, ShopOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { adminLogin, officeLogin } from '../store/slices/authSlice';
import { useTheme } from '../contexts/ThemeContext';
import * as api from '../services/api';

const { Title, Text } = Typography;

const Login: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'admin' | 'office'>('office');
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { theme } = useTheme();

  // Admin login handler
  const onAdminLogin = async (values: { email: string; password: string }) => {
    setLoading(true);
    try {
      const response = await api.adminLogin({
        identifier: values.email,
        password: values.password,
      });

      dispatch(adminLogin({ 
        user: response.user || { id: 0, email: values.email, name: 'Admin', role: 'admin' }, 
        token: response.token 
      }));
      message.success('تم تسجيل دخول المشرف بنجاح');
      navigate('/admin');
    } catch (error: any) {
      message.error(error.message || 'البريد الإلكتروني أو كلمة المرور غير صحيحة');
    } finally {
      setLoading(false);
    }
  };

  // Office login handler
  const onOfficeLogin = async (values: { username: string; password: string }) => {
    setLoading(true);
    try {
      const response = await api.officeLogin({
        username: values.username,
        password: values.password,
      });

      if (response.exchangeOffice) {
        dispatch(officeLogin({ 
          office: response.exchangeOffice, 
          token: response.token 
        }));
        message.success('تم تسجيل الدخول بنجاح');
        navigate('/dashboard');
      } else {
        message.error('حدث خطأ في استلام بيانات المكتب');
      }
    } catch (error: any) {
      message.error(error.message || 'اسم المستخدم أو كلمة المرور غير صحيحة');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ 
      minHeight: '100vh', 
      display: 'flex', 
      justifyContent: 'center', 
      alignItems: 'center',
      background: theme.gradient,
      padding: '20px',
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Decorative background elements */}
      <div style={{
        position: 'absolute',
        top: '-50%',
        right: '-50%',
        width: '100%',
        height: '200%',
        background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)',
        animation: 'pulse 20s ease-in-out infinite',
      }} />
      <div style={{
        position: 'absolute',
        bottom: '-50%',
        left: '-50%',
        width: '100%',
        height: '200%',
        background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)',
        animation: 'pulse 15s ease-in-out infinite',
      }} />

      <Row 
        gutter={[32, 32]} 
        style={{ 
          width: '100%', 
          maxWidth: 1000,
          zIndex: 1,
          background: 'rgba(255,255,255,0.95)',
          borderRadius: 20,
          padding: 40,
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
        }}
        align="middle"
      >
        <Col xs={24} md={12} style={{ textAlign: 'center' }}>
          <div style={{ 
            padding: '40px 20px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%'
          }}>
            <div style={{
              width: 200,
              height: 200,
              borderRadius: '50%',
              background: theme.gradient,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: 30,
              boxShadow: '0 10px 30px rgba(102, 126, 234, 0.4)',
            }}>
              <BankOutlined style={{ fontSize: 100, color: '#fff' }} />
            </div>
            <Title level={2} style={{ color: theme.primary, marginBottom: 10 }}>
              نظام إدارة الصرافة
            </Title>
            <Text style={{ color: theme.textSecondary, fontSize: 16 }}>
              إدارة أسعار الصرف والعملات بسهولة وأمان
            </Text>
            <div style={{ 
              marginTop: 30,
              display: 'flex',
              gap: 20,
              flexWrap: 'wrap',
              justifyContent: 'center'
            }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 32, fontWeight: 'bold', color: theme.primary }}>💰</div>
                <Text style={{ color: theme.textSecondary, fontSize: 12 }}>عملات متعددة</Text>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 32, fontWeight: 'bold', color: theme.primary }}>📊</div>
                <Text style={{ color: theme.textSecondary, fontSize: 12 }}>تقارير شاملة</Text>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 32, fontWeight: 'bold', color: theme.primary }}>🔒</div>
                <Text style={{ color: theme.textSecondary, fontSize: 12 }}>آمن ومحمي</Text>
              </div>
            </div>
          </div>
        </Col>
        <Col xs={24} md={12}>
          <div style={{ textAlign: 'center', marginBottom: 20 }}>
            <Title level={2} style={{ color: theme.primary }}>تسجيل الدخول</Title>
            <Text style={{ color: theme.textMuted }}>أدخل بياناتك للوصول إلى لوحة التحكم</Text>
          </div>
          
          <Tabs
            activeKey={activeTab}
            onChange={(key) => setActiveTab(key as 'admin' | 'office')}
            centered
            items={[
              {
                key: 'office',
                label: (
                  <span>
                    <ShopOutlined /> مكتب صرافة
                  </span>
                ),
                children: (
                  <Form
                    name="office-login"
                    onFinish={onOfficeLogin}
                    layout="vertical"
                    size="large"
                  >
                    <Form.Item
                      name="username"
                      rules={[{ required: true, message: 'يرجى إدخال اسم المستخدم' }]}
                    >
                      <Input
                        prefix={<UserOutlined style={{ color: theme.primary }} />}
                        placeholder="اسم المستخدم"
                        style={{ direction: 'rtl', height: 45 }}
                      />
                    </Form.Item>

                    <Form.Item
                      name="password"
                      rules={[
                        { required: true, message: 'يرجى إدخال كلمة المرور' },
                        { min: 8, message: 'كلمة المرور يجب أن تكون 8 أحرف على الأقل' }
                      ]}
                    >
                      <Input.Password
                        prefix={<LockOutlined style={{ color: theme.primary }} />}
                        placeholder="كلمة المرور"
                        style={{ direction: 'rtl', height: 45 }}
                      />
                    </Form.Item>

                    <Form.Item>
                      <Button
                        type="primary"
                        htmlType="submit"
                        loading={loading}
                        block
                        style={{ 
                          height: 50,
                          background: theme.gradient,
                          border: 'none',
                          fontSize: 16,
                          fontWeight: 'bold'
                        }}
                      >
                        دخول المكتب
                      </Button>
                    </Form.Item>
                  </Form>
                ),
              },
              {
                key: 'admin',
                label: (
                  <span>
                    <BankOutlined /> المشرف
                  </span>
                ),
                children: (
                  <Form
                    name="admin-login"
                    onFinish={onAdminLogin}
                    layout="vertical"
                    size="large"
                  >
                    <Form.Item
                      name="email"
                      rules={[
                        { required: true, message: 'يرجى إدخال البريد الإلكتروني' },
                        { type: 'email', message: 'يرجى إدخال بريد إلكتروني صحيح' }
                      ]}
                    >
                      <Input
                        prefix={<MailOutlined style={{ color: theme.primary }} />}
                        placeholder="البريد الإلكتروني"
                        style={{ direction: 'ltr', height: 45 }}
                      />
                    </Form.Item>

                    <Form.Item
                      name="password"
                      rules={[
                        { required: true, message: 'يرجى إدخال كلمة المرور' },
                        { min: 8, message: 'كلمة المرور يجب أن تكون 8 أحرف على الأقل' }
                      ]}
                    >
                      <Input.Password
                        prefix={<LockOutlined style={{ color: theme.primary }} />}
                        placeholder="كلمة المرور"
                        style={{ direction: 'rtl', height: 45 }}
                      />
                    </Form.Item>

                    <Form.Item>
                      <Button
                        type="primary"
                        htmlType="submit"
                        loading={loading}
                        block
                        style={{ 
                          height: 50,
                          background: 'linear-gradient(135deg, #f5576c 0%, #f093fb 100%)',
                          border: 'none',
                          fontSize: 16,
                          fontWeight: 'bold'
                        }}
                      >
                        دخول المشرف
                      </Button>
                    </Form.Item>
                  </Form>
                ),
              },
            ]}
          />
        </Col>
      </Row>

      <style>{`
        @keyframes pulse {
          0%, 100% { transform: scale(1); opacity: 0.5; }
          50% { transform: scale(1.1); opacity: 0.8; }
        }
      `}</style>
    </div>
  );
};

export default Login;
